'use client';

import { useEffect } from 'react';
import Router from 'next/router';
import { useBodyClasses } from '@/hooks/useBodyClasses';
import { useBackNavigation } from '@/hooks/useBackNavigation';
import { initializeGoogleTagManager } from '@/utils/googleTagManager';
import { CONFIG } from '@/constants/config';
import { noTopInsert } from '@/bridge/utils/noTopInsert';
import { useLoginUser } from '@/hooks/useLoginUser';
import { useFirstInteraction } from '@/hooks/useFirstInteraction';
import { useBannerStore } from '@/store/useBannerStore/useBannerStore';
import { useAdriverBgBannerCleaner } from '@/components/Banners/AdriverBanner.hooks';
import { useNavigation } from '@/hooks/useNavigation';
import { useAdSubmissionFlow } from '@/hooks/useAdSubmissionFlow';

interface ClientWrapperProps {
  children: React.ReactNode;
  isSSRWebView?: boolean;
  accessToken?: string;
}

export function ClientWrapper({
  children,
  isSSRWebView,
  accessToken,
}: ClientWrapperProps) {
  useBodyClasses();
  useBackNavigation();
  useAdriverBgBannerCleaner();
  useNavigation();
  useAdSubmissionFlow();
  const isLoginInProgress = useLoginUser(accessToken);
  const [canLoadBanners, setCanLoadBanners] = useBannerStore(state => [
    state.canLoadBanners,
    state.setCanLoadBanners,
  ]);

  useFirstInteraction(() => setCanLoadBanners(true));

  useEffect(() => {
    if (canLoadBanners) {
      initializeGoogleTagManager(CONFIG.gtmContainerId);
    }

    if (isSSRWebView) {
      noTopInsert();
      Router.events.on('beforeHistoryChange', noTopInsert);
    }

    if (!document.querySelector('script[src*="gpt.js"]') && canLoadBanners) {
      const script = document.createElement('script');
      script.src = 'https://securepubads.g.doubleclick.net/tag/js/gpt.js';
      script.async = true;
      document.head.appendChild(script);
    }

    return () => {
      if (isSSRWebView) {
        Router.events.off('beforeHistoryChange', noTopInsert);
      }
    };
  }, [isSSRWebView, canLoadBanners]);

  if (isLoginInProgress) return null;

  return <>{children}</>;
}
